<script>
import * as m from '@app/paraglide/messages.js';
import { auth, functions } from '../configs/firebase'
import {
    GoogleAuthProvider,
    signInWithCredential,
    signInWithPopup,
    getAdditionalUserInfo,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { replace } from 'svelte-spa-router'
import { onMount, onDestroy } from 'svelte'
import { environment } from '../store/envStore.svelte.js';

// Check URL query params for status
// Example URL: https://app.closedcaptioncreator.com/#/login?status=signup
// For hash-based routing, query params come after the #, so we parse from the hash
const hash = window.location.hash; // e.g., "#/login?status=signup"
const hashQueryIndex = hash.indexOf('?');
const hashQueryString = hashQueryIndex !== -1 ? hash.substring(hashQueryIndex) : '';
const urlParams = new URLSearchParams(hashQueryString);
const statusParam = urlParams.get('status');

// State management
let email = '';
let password = '';
let confirmPassword = '';
let isSignUp = statusParam === 'signup'; // Default to signup if status=signup in URL
let isLoading = false;
let errorMessage = '';
let successMessage = '';
let showEmailVerification = false;
let showEmailForm = false;
let currentUser = null;
let awaitingExternalAuth = false;

// Form validation
let emailError = '';
let passwordError = '';

// Email validation function
function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

function validatePassword(password, { requireComplexity = false } = {}) {
    if (requireComplexity) {
        return (
            password.length >= 10 &&
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            /\d/.test(password) &&
            /[^A-Za-z0-9]/.test(password)
        );
    }
    return password.length >= 6;
}

// Clear messages
function clearMessages() {
    errorMessage = '';
    successMessage = '';
    emailError = '';
    passwordError = '';
}

// Check if user has an active subscription
async function checkUserSubscription(email) {
    try {
        const getAuthInfoRes = await httpsCallable(functions, 'v8UserAuthV3')(email);
        const subscriptionList = getAuthInfoRes.data.subscriptions.list;
        return subscriptionList.length > 0;
    } catch (error) {
        console.error('Error checking subscription:', error);
        // On error, assume no subscription and let them go to signup
        return false;
    }
}

// Firebase Auth 12.17.x closes IndexedDB while the document is hidden.
// Electron Google login opens the system browser, so wait until this
// window is visible again before persisting the credential.
function waitForDocumentVisible() {
    if (document.visibilityState === 'visible') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const onChange = () => {
            if (document.visibilityState === 'visible') {
                document.removeEventListener('visibilitychange', onChange);
                resolve();
            }
        };
        document.addEventListener('visibilitychange', onChange);
    });
}

function isAuthDbClosingError(error) {
    return error?.message?.includes('Database is closing/hidden');
}

async function signInWithGoogleCredential(credential) {
    await waitForDocumentVisible();

    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            return await signInWithCredential(auth, credential);
        } catch (error) {
            if (!isAuthDbClosingError(error) || attempt === maxAttempts - 1) {
                throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, 100 * (attempt + 1)));
        }
    }
}

// Sign in with Google
async function signInWithGoogle() {
    clearMessages();
    isLoading = true;
    
    try {
        let result;
        if (environment.electron) {
            awaitingExternalAuth = true;
            const { idToken, accessToken } = await window.auth.handleGoogleLogin();
            const credential = GoogleAuthProvider.credential(idToken, accessToken);
            result = await signInWithGoogleCredential(credential);
            awaitingExternalAuth = false;
        } else {
            const provider = new GoogleAuthProvider();
            result = await signInWithPopup(auth, provider);
        }
        
        // Check if this is a new user signing up
        if (getAdditionalUserInfo(result)?.isNewUser) {
            // New user - check for subscription
            const hasSubscription = await checkUserSubscription(result.user.email);
            if (hasSubscription) {
                replace("/");
            } else {
                replace("/signup");
            }
        } else {
            // Existing user - go to main app
            replace("/");
        }
    } catch (error) {
        console.error('Google sign-in error:', error);
        awaitingExternalAuth = false;
        
        // Handle timeout error
        if (error.message?.includes('timed out')) {
            errorMessage = m.login_error_signin_timeout();
        } else {
            switch (error.code) {
                case 'auth/popup-closed-by-user':
                    // User closed popup, no error message needed
                    break;
                case 'auth/popup-blocked':
                    errorMessage = m.login_error_popup_blocked();
                    break;
                case 'auth/account-exists-with-different-credential':
                    errorMessage = m.login_error_account_exists_different();
                    break;
                case 'auth/cancelled-popup-request':
                    // Multiple popup requests, ignore
                    break;
                default:
                    errorMessage = m.login_error_generic();
            }
        }
    } finally {
        isLoading = false;
    }
}

// Go back to auth method selection
function goBackToMethodSelection() {
    clearMessages();
    showEmailForm = false;
    password = '';
    confirmPassword = '';
    // Preserve email
}

// Continue with email - show email form
function continueWithEmail() {
    clearMessages();
    
    if (!validateEmail(email)) {
        emailError = m.login_error_email_invalid();
        return;
    }
    
    showEmailForm = true;
}

// Handle form submission
async function handleSubmit() {
    clearMessages();
    
    // Validate inputs
    if (!validateEmail(email)) {
        emailError = m.login_error_email_invalid();
        return;
    }
    
    if (!validatePassword(password, { requireComplexity: isSignUp })) {
        passwordError = isSignUp
            ? m.login_error_password_policy()
            : m.login_error_password_length();
        return;
    }
    
    if (isSignUp && password !== confirmPassword) {
        passwordError = m.login_error_password_mismatch();
        return;
    }
    
    isLoading = true;
    
    try {
        if (isSignUp) {
            // Create new user
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            currentUser = userCredential.user;
            
            // Send email verification
            await sendEmailVerification(currentUser);
            showEmailVerification = true;
            successMessage = m.login_success_account_created();
        } else {
            // Sign in existing user
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            if (!user.emailVerified) {                
                showEmailVerification = true;
                currentUser = user;
                await sendEmailVerification(currentUser);
                successMessage = m.login_success_verify_email();                
            } else {
                // User is verified, redirect to app
                replace("/");
            }
        }
    } catch (error) {
        console.error('Authentication error:', error);
        switch (error.code) {
            case 'auth/email-already-in-use':
                errorMessage = m.login_error_email_exists();
                break;
            case 'auth/invalid-email':
                errorMessage = m.login_error_invalid_email();
                break;
            case 'auth/operation-not-allowed':
                errorMessage = m.login_error_operation_not_allowed();
                break;
            case 'auth/weak-password':
            case 'auth/password-does-not-meet-requirements':
                errorMessage = m.login_error_password_policy();
                break;
            case 'auth/user-disabled':
                errorMessage = m.login_error_user_disabled();
                break;
            case 'auth/user-not-found':
                errorMessage = m.login_error_user_not_found();
                break;
            case 'auth/wrong-password':
                errorMessage = m.login_error_wrong_password();
                break;
            case 'auth/invalid-credential':
                errorMessage = m.login_error_invalid_credential();
                break;
            default:
                errorMessage = m.login_error_generic();
        }
    } finally {
        isLoading = false;
    }
}

// Resend email verification
async function resendVerificationEmail() {
    if (!currentUser) return;
    
    try {
        await sendEmailVerification(currentUser);
        successMessage = m.login_success_verification_sent();
    } catch (error) {
        errorMessage = m.login_error_verification_failed();
    }
}

// Check email verification status
async function checkEmailVerification() {
    if (!currentUser) return;
    
    try {
        await currentUser.reload();
        if (currentUser.emailVerified) {
            // If this was a sign-up flow, check for subscription
            if (isSignUp) {
                const hasSubscription = await checkUserSubscription(currentUser.email);
                if (hasSubscription) {
                    replace("/");
                } else {
                    replace("/signup");
                }
            } else {
                // Existing user signing in - go to main app
                replace("/");
            }
        } else {
            errorMessage = m.login_error_not_verified();
        }
    } catch (error) {
        errorMessage = m.login_error_check_verification();
    }
}

// Toggle between sign in and sign up
function toggleMode() {
    clearMessages();
    isSignUp = !isSignUp;
    confirmPassword = '';
    showEmailVerification = false;
    showEmailForm = false;
}

// Reset password
async function resetPassword() {
    clearMessages();
    
    if (!validateEmail(email)) {
        emailError = m.login_error_email_validation();
        return;
    }
    
    try {
        await sendPasswordResetEmail(auth, email);
        successMessage = m.login_success_password_reset();
    } catch (error) {
        errorMessage = m.login_error_password_reset();
    }
}

onMount(() => {
    // No specific setup needed for custom auth
});

onDestroy(() => {
    // Cleanup if needed
});
</script>

<div id="LoginScreen">
    <div class="container d-flex justify-content-center align-items-center LoginPageWrapper">
        <div class="col-md-5 col-sm-8 col-11 shadow LoginFormWrapper text-white rounded border-0">
            <div class="d-flex justify-content-center align-items-center m-3 mb-4">
                <img src="./cc-logo.png" width="200px" alt={m.app_logo_alt()} class="mb-1"/>
            </div>
            
            {#if showEmailVerification}
                <!-- Email Verification Screen -->
                <div class="verification-container text-center">
                    <h4 class="mb-3">{m.login_verify_email_title()}</h4>
                    <p class="mb-4">
                        {@html m.login_verify_email_message({email})}
                    </p>
                    
                    <div class="d-grid gap-2 mb-3">
                        <button type="button" class="btn btn-primary" onclick={checkEmailVerification}>
                            {m.login_verified_button()}
                        </button>
                        <button type="button" class="btn btn-outline-light" onclick={resendVerificationEmail}>
                            {m.login_resend_verification_button()}
                        </button>
                        <button type="button" class="btn btn-outline-secondary" onclick={() => showEmailVerification = false}>
                            {m.login_back_to_login_button()}
                        </button>
                    </div>
                </div>
            {:else if awaitingExternalAuth}
                <!-- Waiting for External Authentication -->
                <div class="external-auth-container text-center">
                    <div class="spinner-border text-light mb-4" role="status" style="width: 3rem; height: 3rem;">
                        <span class="visually-hidden">{m.loading()}</span>
                    </div>
                    <h4 class="mb-3">{m.login_complete_signin_title()}</h4>
                    <p class="mb-4 small">
                        {m.login_complete_signin_message()}
                    </p>
                    <p class="text-muted small mb-4">
                        {m.login_complete_signin_waiting()}
                    </p>
                    <button type="button" class="btn btn-outline-secondary" onclick={() => { awaitingExternalAuth = false; isLoading = false; }}>
                        {m.login_cancel_button()}
                    </button>
                </div>
            {:else}
                <!-- Login/Signup Flow -->
                <h3 class="text-center mb-4">{isSignUp ? m.login_create_account_title() : m.login_sign_in_title()}</h3>
                
                <!-- Error Message -->
                {#if errorMessage}
                    <div class="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                {/if}
                
                <!-- Success Message -->
                {#if successMessage}
                    <div class="alert alert-success" role="alert">
                        {successMessage}
                    </div>
                {/if}
                
                {#if !showEmailForm}
                    <!-- Auth Method Selection -->
                    <div class="auth-method-selection">
                        <!-- Google Sign In Button -->
                        <div class="d-grid gap-2 mb-3">
                            <button type="button" class="btn btn-google" onclick={signInWithGoogle} disabled={isLoading}>
                                {#if isLoading}
                                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {m.login_signing_in_button()}
                                {:else}
                                    <svg class="google-icon" viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                                        <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                                            <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                                            <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                                            <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                                            <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                                        </g>
                                    </svg>
                                    {m.login_continue_with_google()}
                                {/if}
                            </button>
                        </div>
                        
                        <!-- Divider -->
                        <div class="divider-or">
                            <span>{m.login_or_divider()}</span>
                        </div>
                        
                        <!-- Email Input -->
                        <form onsubmit={(e) => { e.preventDefault(); continueWithEmail(); }}>
                            <div class="mb-3">
                                <label for="email" class="form-label">{m.login_email_label()}</label>
                                <input 
                                    type="email" 
                                    class="form-control {emailError ? 'is-invalid' : ''}" 
                                    id="email" 
                                    bind:value={email}
                                    placeholder={m.login_email_placeholder()}
                                    required
                                />
                                {#if emailError}
                                    <div class="invalid-feedback">{emailError}</div>
                                {/if}
                            </div>
                            
                            <div class="d-grid gap-2 mb-3">
                                <button type="submit" class="btn btn-primary">
                                    {m.login_continue_with_email_button()}
                                </button>
                            </div>
                        </form>
                        
                        <!-- Toggle Sign In/Sign Up -->
                        <div class="text-center">
                            <p class="mb-2">
                                {isSignUp ? m.login_already_have_account() : m.login_dont_have_account()}
                            </p>
                            <button type="button" class="btn btn-outline-light" onclick={toggleMode}>
                                {isSignUp ? m.login_sign_in_button() : m.login_create_account_button()}
                            </button>
                        </div>
                    </div>
                {:else}
                    <!-- Email/Password Form -->
                    <div class="email-form">
                        <!-- Back Button -->
                        <button type="button" class="back-link mb-3" onclick={goBackToMethodSelection}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fill-rule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                            </svg>
                            {m.login_back_button()}
                        </button>
                        
                        <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <!-- Email Field (readonly, already entered) -->
                            <div class="mb-3">
                                <label for="email-display" class="form-label">{m.login_email_label()}</label>
                                <input 
                                    type="email" 
                                    class="form-control" 
                                    id="email-display" 
                                    value={email}
                                    readonly
                                />
                            </div>
                            
                            <!-- Password Field -->
                            <div class="mb-3">
                                <label for="password" class="form-label">{m.login_password_label()}</label>
                                <input 
                                    type="password" 
                                    class="form-control {passwordError ? 'is-invalid' : ''}" 
                                    id="password" 
                                    bind:value={password}
                                    placeholder={m.login_password_placeholder()}
                                    required
                                />
                                {#if passwordError}
                                    <div class="invalid-feedback">{passwordError}</div>
                                {/if}
                                {#if isSignUp && !passwordError}
                                    <div class="form-text text-muted">{m.login_password_policy_hint()}</div>
                                {/if}
                            </div>
                            
                            <!-- Confirm Password Field (Sign Up Only) -->
                            {#if isSignUp}
                                <div class="mb-3">
                                    <label for="confirmPassword" class="form-label">{m.login_confirm_password_label()}</label>
                                    <input 
                                        type="password" 
                                        class="form-control" 
                                        id="confirmPassword" 
                                        bind:value={confirmPassword}
                                        placeholder={m.login_confirm_password_placeholder()}
                                        required
                                    />
                                </div>
                            {/if}
                            
                            <!-- Submit Button -->
                            <div class="d-grid gap-2 mb-3">
                                <button type="submit" class="btn btn-primary" disabled={isLoading}>
                                    {#if isLoading}
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {isSignUp ? m.login_creating_account_button() : m.login_signing_in_button_2()}
                                    {:else}
                                        {isSignUp ? m.login_create_account_button() : m.login_sign_in_button()}
                                    {/if}
                                </button>
                            </div>
                            
                            <!-- Forgot Password -->
                            {#if !isSignUp}
                                <div class="text-center mb-3">
                                    <button type="button" class="btn btn-link text-muted" onclick={resetPassword}>
                                        {m.login_forgot_password_button()}
                                    </button>
                                </div>
                            {/if}
                        </form>
                    </div>
                {/if}
            {/if}
            
            <small class="form-text text-center text-white small">
                {m.login_support_text()}
            </small>
        </div>
    </div>
</div>

<style lang="css">
#LoginScreen {
    position: relative;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    color: #f0f0f0;
    font-family: 'Roboto', sans-serif;
    background: #000000;
    background: linear-gradient(154deg,rgba(0, 0, 0, 1) 0%, rgba(6, 71, 57, 1) 100%);
}

.LoginPageWrapper {
    height: 100vh;
    position: relative;
    z-index: 1;
}

.LoginFormWrapper {
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    padding: 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    max-height: 90vh;
    overflow-y: auto;
}

/* Form Styling */
.form-label {
    color: #f0f0f0;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

.form-control {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #f0f0f0;
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.3s ease;
}

.form-control:focus {
    background-color: rgba(255, 255, 255, 0.15);
    border-color: #089264;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
    color: #f0f0f0;
}

.form-control::placeholder {
    color: rgba(240, 240, 240, 0.6);
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    color: #ff6b6b;
    font-size: 0.875rem;
}

/* Button Styling */
.btn-primary {
    font-weight: 500;
    transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #009978, #00853c);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(13, 253, 221, 0.3);
}

.btn-primary:disabled {
    background: rgba(13, 253, 181, 0.6);
    transform: none;
    box-shadow: none;
}

.btn-outline-light {
    border-color: rgba(255, 255, 255, 0.5);
    color: #f0f0f0;
    border-radius: 8px;
    padding: 10px 20px;
    transition: all 0.3s ease;
}

.btn-outline-light:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
    color: #f0f0f0;
}

.btn-outline-secondary {
    border-color: rgba(108, 117, 125, 0.7);
    color: rgba(108, 117, 125, 1);
    border-radius: 8px;
    padding: 10px 20px;
    transition: all 0.3s ease;
}

.btn-outline-secondary:hover {
    background-color: rgba(108, 117, 125, 0.1);
    border-color: rgba(108, 117, 125, 1);
    color: rgba(108, 117, 125, 1);
}

.btn-link {
    color: rgba(240, 240, 240, 0.8);
    text-decoration: none;
    font-size: 0.9rem;
    padding: 0;
}

.btn-link:hover {
    color: #f0f0f0;
    text-decoration: underline;
}

/* Alert Styling */
.alert {
    border-radius: 8px;
    border: none;
    padding: 12px 16px;
    margin-bottom: 1.5rem;
}

.alert-danger {
    background-color: rgba(220, 53, 69, 0.2);
    color: #ff6b6b;
    border-left: 4px solid #dc3545;
}

.alert-success {
    background-color: rgba(25, 135, 84, 0.2);
    color: #6bcf7f;
    border-left: 4px solid #198754;
}

/* Verification Container */
.verification-container {
    padding: 20px 0;
}

.verification-container h4 {
    color: #f0f0f0;
    font-weight: 500;
}

.verification-container p {
    color: rgba(240, 240, 240, 0.8);
    line-height: 1.5;
}

/* Loading Spinner */
.spinner-border-sm {
    width: 1rem;
    height: 1rem;
}

small {
    display: block;
    margin-top: 25px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
}

/* Make UI responsive */
@media (max-width: 768px) {
    .LoginFormWrapper {
        padding: 30px 20px;
        margin: 10px;
    }
    
    .form-control {
        font-size: 16px; /* Prevents zoom on iOS */
    }
}

/* Custom scrollbar for form wrapper */
.LoginFormWrapper::-webkit-scrollbar {
    width: 6px;
}

.LoginFormWrapper::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
}

.LoginFormWrapper::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
}

.LoginFormWrapper::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}

/* Google Sign-In Button */
.btn-google {
    background-color: #fff;
    color: #757575;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 12px 20px;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-google:hover:not(:disabled) {
    background-color: #f8f9fa;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
}

.btn-google:disabled {
    background-color: rgba(255, 255, 255, 0.7);
    color: #999;
}

.google-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
}

/* External Auth Waiting Container */
.external-auth-container {
    padding: 40px 20px;
}

.external-auth-container h4 {
    color: #f0f0f0;
    font-weight: 500;
}

.external-auth-container p {
    color: rgba(240, 240, 240, 0.8);
    line-height: 1.6;
}

/* Divider with "or" text */
.divider-or {
    display: flex;
    align-items: center;
    margin: 24px 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;
}

.divider-or::before,
.divider-or::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
}

.divider-or span {
    padding: 0 16px;
}

/* Back Link */
.back-link {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: color 0.2s ease;
}

.back-link:hover {
    color: #fff;
}

.back-link svg {
    width: 16px;
    height: 16px;
}

/* Email form readonly field */
.email-form .form-control[readonly] {
    background-color: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.8);
    cursor: default;
}
</style>
